import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { Chip, Typography } from '@mui/joy';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { AccountTreeRounded } from '@mui-symbols-material/w300';
import {WidgetBox} from "@dreebit-registry/vsm-microservice-components";
import AssetProductImage from '../AssetProductImage';
import { getStatusChipColors, getStatusChipLabel } from '../AssetNameplate';
import { InstalledBaseNodeList } from '../../graphql/queries/installedBaseNodeList';
import { SystemInstalledBaseNodeId } from '../../graphql/queries/systemInstalledBaseNodeId';
import { InstalledBaseNodeTypeKey } from '../../codegen/graphql';
import { IndustryType } from '../../hooks/useIndustryType';
import { SkeletonListRows, fadeInSx } from './HeroSkeletons';
import { HeroCardHeader } from './heroPrimitives';

interface Props {
    systemId: string;
}

/** One asset rendered as a compact nameplate card with a connection-status chip (IoT statuses only). */
const PressureLineAssetCard = ({ node }: { node: any }) => {
    const { t } = useTranslation();
    const connectionStatus = _.get(node, 'assetLocation.iotConnection.connectionStatus');
    const serialNumber = _.get(node, 'assetLocation.asset.serialNumber');
    return (
        <Box sx={{ width: 160, minWidth: 0, border: '1px solid #e0e0e0', borderRadius: '8px', p: '12px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', textAlign: 'center' }}>
            <AssetProductImage industryType={IndustryType.INDUSTRY} style={{ width: 56, height: 56 }} />
            <Typography level="bodyBold" sx={{ color: '#333', maxWidth: '100%' }} noWrap>
                {node.title}
            </Typography>
            {serialNumber && (
                <Typography level="bodySmall" sx={{ color: '#777', maxWidth: '100%' }} noWrap>
                    {serialNumber}
                </Typography>
            )}
            {connectionStatus && (() => {
                const { bg, text } = getStatusChipColors(connectionStatus);
                return (
                    <Chip size="sm" sx={{ backgroundColor: bg, color: text, maxWidth: '100%' }}>
                        {getStatusChipLabel(t, connectionStatus)}
                    </Chip>
                );
            })()}
        </Box>
    );
};

/** Skeleton mirroring a row of nameplate cards while the child-asset batch loads. */
const AssetCardsSkeleton = () => (
    <>
        <Skeleton variant="rounded" width={160} height={148} sx={{ borderRadius: '8px' }} />
        <Skeleton variant="rounded" width={160} height={148} sx={{ borderRadius: '8px' }} />
    </>
);

/** One pressure line — purely presentational; child assets come from the shared batch query. */
const PressureLine = ({ line, assets, assetsLoading }: { line: any; assets: any[]; assetsLoading: boolean }) => {
    const { t } = useTranslation();
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', pb: '10px', borderBottom: '1px solid #eee' }}>
                <Typography level="titleSmall" sx={{ color: '#333' }}>
                    {line.title}
                </Typography>
                <Chip size="sm" variant="soft" color="neutral" sx={{ ml: 'auto' }}>
                    {t('{{count}} assets', { count: line.assetCount ?? assets.length })}
                </Chip>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '12px', pt: '12px' }}>
                {assetsLoading ? (
                    <AssetCardsSkeleton />
                ) : assets.length === 0 ? (
                    <Typography level="bodySmall" sx={{ color: '#777' }}>
                        {t('No assets')}
                    </Typography>
                ) : (
                    assets.map((a) => (
                        <Box key={a.id} sx={fadeInSx}>
                            <PressureLineAssetCard node={a} />
                        </Box>
                    ))
                )}
            </Box>
        </Box>
    );
};

/**
 * Full-width pressure-lines overview for a system: each line lists its child assets as
 * nameplate cards. Lines are fetched under the system's installed-base node; the child
 * assets of ALL lines arrive in one batched query (parentNodeId in) instead of one
 * request per line, so the lines fill together.
 */
const PressureLinesWidget = ({ systemId }: Props) => {
    const { t } = useTranslation();

    const nodeQuery = useQuery(SystemInstalledBaseNodeId, {
        variables: { id: systemId },
        skip: !systemId,
    });
    const systemNodeId = _.get(nodeQuery, 'data.system.installedBaseNode.id');

    const linesQuery = useQuery(InstalledBaseNodeList, {
        variables: {
            filter: {
                parentNodeId: { equals: systemNodeId },
                nodeTypeKey: { equals: InstalledBaseNodeTypeKey.PRESSURE_LINE },
            },
            pagination: { start: 0, limit: 50 },
        },
        skip: !systemNodeId,
    });
    const lines: any[] = _.get(linesQuery, 'data.installedBaseNodeList.installedBaseNodes', []) ?? [];
    const lineIds = lines.map((line) => line.id);

    const childrenQuery = useQuery(InstalledBaseNodeList, {
        variables: {
            filter: { parentNodeId: { in: lineIds } },
            pagination: { start: 0, limit: 250 },
        },
        skip: lineIds.length === 0,
    });
    const children: any[] = _.get(childrenQuery, 'data.installedBaseNodeList.installedBaseNodes', []) ?? [];
    const assetsByLine = _.groupBy(
        children.filter((n) => _.get(n, 'assetLocation.id')),
        (n) => _.get(n, 'parentNode.id'),
    );

    const linesPending = (nodeQuery.loading && !nodeQuery.data) || (linesQuery.loading && !linesQuery.data);
    const childrenPending = childrenQuery.loading && !childrenQuery.data;

    return (
        <WidgetBox sx={{ mb: '16px' }}>
            <HeroCardHeader
                title={t('Pressure lines')}
                icon={<AccountTreeRounded />}
                count={lines.length > 0 ? lines.length : undefined}
            />
            <Box sx={{ mt: '12px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {linesPending ? (
                    <SkeletonListRows rows={2} />
                ) : lines.length === 0 ? (
                    <Typography level="bodySmall" sx={{ color: '#777' }}>
                        {t('No pressure lines')}
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', ...fadeInSx }}>
                        {lines.map((line) => (
                            <PressureLine
                                key={line.id}
                                line={line}
                                assets={assetsByLine[line.id] ?? []}
                                assetsLoading={childrenPending}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </WidgetBox>
    );
};

export default PressureLinesWidget;
